// Constants for UnifiedCourseModal

export interface GradeOption {
  value: string;
  label: string;
}

/** UMD fallback only — components use `useGradeOptions()` (W43 P3), which
 *  builds the list from the active institution's grade scale. */
export const GRADE_OPTIONS: GradeOption[] = [
  { value: '', label: 'No Grade' },
  { value: 'A+', label: 'A+' },
  { value: 'A', label: 'A' },
  { value: 'A-', label: 'A-' },
  { value: 'B+', label: 'B+' },
  { value: 'B', label: 'B' },
  { value: 'B-', label: 'B-' },
  { value: 'C+', label: 'C+' },
  { value: 'C', label: 'C' },
  { value: 'C-', label: 'C-' },
  { value: 'D+', label: 'D+' },
  { value: 'D', label: 'D' },
  { value: 'D-', label: 'D-' },
  { value: 'F', label: 'F' },
  { value: 'P', label: 'P (Pass)' },
  { value: 'CR', label: 'CR (Credit)' },
  { value: 'W', label: 'W (Withdrawn)' },
];

export interface SourceTypeOption {
  value: string;
  label: string;
}

export const SOURCE_TYPES: SourceTypeOption[] = [
  { value: 'community_college', label: 'Community College' },
  { value: 'ib', label: 'IB Exam' },
  { value: 'clep', label: 'CLEP Exam' },
  { value: 'military', label: 'Military Credit' },
  { value: 'other_institution', label: 'Other Institution' },
  { value: 'other', label: 'Other' },
];

export interface PlaceholderTemplateData {
  title: string;
  credits: number;
  placeholderType: string;
  requirements: {
    department?: string;
    levelMin?: number;
    levelMax?: number;
    genEdCategories?: string[];
    [key: string]: unknown;
  };
}

export interface PlaceholderTemplate {
  name: string;
  data: PlaceholderTemplateData;
}

export const PLACEHOLDER_TEMPLATES: PlaceholderTemplate[] = [
  {
    name: 'Upper-level CS Elective',
    data: {
      title: 'Upper-level CS Elective',
      credits: 3,
      placeholderType: 'elective',
      requirements: { department: 'CMSC', levelMin: 400, levelMax: 499 },
    },
  },
  {
    name: 'Technical Elective',
    data: {
      title: 'Technical Elective',
      credits: 3,
      placeholderType: 'technical',
      requirements: {},
    },
  },
  {
    name: 'Natural Science GenEd',
    data: {
      title: 'Natural Science with Lab',
      credits: 4,
      placeholderType: 'genEd',
      requirements: { genEdCategories: ['DSNL'] },
    },
  },
  {
    name: 'Humanities GenEd',
    data: {
      title: 'Humanities GenEd',
      credits: 3,
      placeholderType: 'genEd',
      requirements: { genEdCategories: ['DSHU'] },
    },
  },
  {
    name: 'Free Elective',
    data: {
      title: 'Free Elective',
      credits: 3,
      placeholderType: 'elective',
      requirements: {},
    },
  },
];

/**
 * Filter `PLACEHOLDER_TEMPLATES` to those that make sense for an institution
 * with the given gen-ed code list. A template with no `genEdCategories` (or an
 * empty list) is institution-agnostic and always passes through. A template
 * that seeds specific GE codes is kept only if every code is present in the
 * institution's list — under any other condition the template would seed an
 * unrecognized requirement. Today this filters out UMD's `DSNL`/`DSHU`-coded
 * templates for institutions that don't use those codes.
 *
 * The same rule applies to a seeded `department` when `institutionDepartmentCodes`
 * is given: "Upper-level CS Elective" seeds UMD's `CMSC`, so it shows only where
 * a CMSC department exists. Omit the argument to skip the department check.
 * Technical Elective and Free Elective seed neither and always show.
 */
export const filterPlaceholderTemplates = (
  templates: PlaceholderTemplate[],
  institutionGenEdCodes: string[],
  institutionDepartmentCodes?: string[],
): PlaceholderTemplate[] => {
  const codeSet = new Set(institutionGenEdCodes);
  const deptSet = institutionDepartmentCodes ? new Set(institutionDepartmentCodes) : null;
  return templates.filter((t) => {
    const dept = t.data.requirements.department;
    if (deptSet && dept && !deptSet.has(dept)) return false;
    const required = t.data.requirements.genEdCategories;
    if (!required || required.length === 0) return true;
    return required.every((c) => codeSet.has(c));
  });
};

// GEN_ED_CATEGORIES moved to InstitutionConfigContext (workstream E).
// PlaceholderForm now reads the per-institution GE list from the context.
// Defensive fallback for UMD lives in src/fallbacks/umdGenEdAreas.ts.
//
// Type re-exported here for backward compat with any code that still
// imports `GenEdCategory` from this file. The canonical type is
// `GenEdArea` in src/fallbacks/umdGenEdAreas.ts; the two shapes are
// compatible (this file's interface is a structural subset).
export type { GenEdArea as GenEdCategory } from '../../../../fallbacks/umdGenEdAreas';
